import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { isNative } from "../lib/runtime";
import type {
  Agency, Department, Employee, RequestField, RequestFieldScope,
  RequestFieldType, ShiftCode, User,
} from "../lib/types";
import { can, LEVEL_LABEL } from "../lib/permissions";
import { OWNERSHIP_CONFIRM_SENTENCE } from "../lib/api";
import { useEffectiveUser } from "../lib/auth";
import { Button } from "../components/ui/Button";
import { Field, Input, Select } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { RemoveButton } from "../components/ui/RemoveButton";
import { BetaFeaturesPanel } from "../components/BetaFeaturesPanel";
import { NotificationTestPanel } from "../components/NotificationTestPanel";
import { WebPushPanel } from "../components/WebPushPanel";
import { AccountDeletionPanel } from "../components/AccountDeletionPanel";
import { AccountDeletionRequestsPanel } from "../components/AccountDeletionRequestsPanel";

export function Settings() {
  const qc = useQueryClient();
  const me = useEffectiveUser();
  const agency = useQuery({ queryKey: ["agency"], queryFn: api.getAgency });
  const [draft, setDraft] = useState<Agency | null>(null);
  // Tac mode is Owner/L1-only — the toggle disappears for L2+ even
  // though they can see the rest of Agency settings.
  const canFlipTac = me?.level === "owner" || me?.level === "l1";
  const tacMut = useMutation({
    mutationFn: (args: { active: boolean; startDate: string | null }) =>
      api.setTacModeActive(args.active, args.startDate),
    // Re-materialisation rewrites auto-XXX assignments for every
    // follows-tac officer, so refresh the schedule data alongside
    // the agency flag.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agency"] });
      qc.invalidateQueries({ queryKey: ["assignments"] });
      qc.invalidateQueries({ queryKey: ["coverage"] });
    },
  });

  useEffect(() => {
    if (agency.data && !draft) setDraft(agency.data);
  }, [agency.data, draft]);

  const saveMut = useMutation({
    mutationFn: () => api.updateAgency({
      name: draft!.name,
      timezone: draft!.timezone,
      weekStart: draft!.weekStart,
      minStaffing: draft!.minStaffing,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agency"] }),
  });

  if (!draft) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  // Every signed-in user reaches Settings now (for browser
  // notifications, iOS design toggle, delete account, etc.), but
  // the panels inside are individually level-gated below. See
  // permissions.ts:
  //   canConfig   → L2+ (agency card, backups, departments,
  //                 shift codes, request fields, shift bid)
  //   canDirector → L1+ (pay-period markers, seniority mode,
  //                 subscription)
  const canConfig = can.editAgencyConfig(me);
  const canDirector = can.editDirectorSettings(me);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">
          {canConfig ? "Agency settings" : "Settings"}
        </h1>
        <p className="text-sm text-slate-500">
          {canConfig
            ? "Settings are shared across every user in this agency."
            : "Personal preferences and account actions."}
        </p>
      </header>

      {canConfig && (
      <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
        <Field label="Agency name">
          <Input value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </Field>
        <Field label="Timezone (IANA, e.g. America/Los_Angeles)">
          <Input value={draft.timezone}
            onChange={(e) => setDraft({ ...draft, timezone: e.target.value })} />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Week starts on">
            <Select value={String(draft.weekStart)}
              onChange={(e) => setDraft({ ...draft, weekStart: Number(e.target.value) })}>
              <option value="0">Sunday</option>
              <option value="1">Monday</option>
            </Select>
          </Field>
          <Field label="Default min staffing">
            <Input type="number" min={0} value={draft.minStaffing}
              onChange={(e) => setDraft({ ...draft, minStaffing: Number(e.target.value) })} />
          </Field>
        </div>
        <BrandingRow />
        {canFlipTac && (
          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800">
                  12-hour Shifts
                </div>
                <p className="mt-0.5 text-xs text-slate-500">
                  When active, the schedule hides regular shifts and shows
                  only 12-hour + exempt shifts. Officers flagged "Uses
                  12-hour Shifts" swap to their 12-hour home shift and
                  12-hour days off. Set an <em>active from</em> date to
                  schedule the switchover — the schedule will render
                  regular shifts before that date and 12-hour on/after,
                  with the transition page showing both side-by-side.
                  Leave the date blank for "12-hour Shifts everywhere."
                  Flip back off any time to restore the regular schedule —
                  nothing is overwritten.
                </p>
              </div>
              <label className="flex items-center gap-2 shrink-0 select-none cursor-pointer">
                <input
                  type="checkbox"
                  checked={agency.data?.tacModeActive === true}
                  disabled={tacMut.isPending}
                  onChange={(e) => tacMut.mutate({
                    active: e.target.checked,
                    // Preserve any already-saved start date when
                    // re-activating; let the server clear it on
                    // deactivate (see api.setTacModeActive).
                    startDate: e.target.checked
                      ? (agency.data?.tacModeStartDate ?? null)
                      : null,
                  })}
                  className="h-4 w-4"
                />
                <span className={`text-xs font-semibold uppercase tracking-wider ${
                  agency.data?.tacModeActive ? "text-amber-700" : "text-slate-500"
                }`}>
                  {agency.data?.tacModeActive ? "On" : "Off"}
                </span>
              </label>
            </div>
            {agency.data?.tacModeActive && (
              <div className="mt-3 flex items-end gap-2">
                <Field label="Active from (optional)">
                  <Input
                    type="date"
                    value={agency.data.tacModeStartDate ?? ""}
                    disabled={tacMut.isPending}
                    onChange={(e) => tacMut.mutate({
                      active: true,
                      startDate: e.target.value || null,
                    })}
                  />
                </Field>
                {agency.data.tacModeStartDate && (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={tacMut.isPending}
                    onClick={() => tacMut.mutate({
                      active: true,
                      startDate: null,
                    })}
                  >
                    Clear date (tac everywhere)
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
            {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
      )}

      {/* Two-column grid on desktop, single-column on mobile.
        * Every panel is level-gated so non-qualifying users just
        * don't take a slot in the grid; the layout reflows.
        *
        * Level access map:
        *   L1+  (canDirector) — Subscription, Transfer Ownership,
        *                        Pay-weekend markers, Seniority mode,
        *                        Beta features, Notification test
        *   L2+  (canConfig)   — Account deletion requests, Backups,
        *                        Departments, Shift codes, Request
        *                        fields, Shift bid
        *   All            — iOS design, Web push (browser
        *                        notifications), Account deletion */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {canDirector && <SubscriptionPanel />}
        {canDirector && <TransferOwnershipPanel />}
        {canConfig && <AccountDeletionRequestsPanel />}
        <WebPushPanel />
        {canDirector && <BetaFeaturesPanel />}
        {canDirector && <NotificationTestPanel />}
        <AccountDeletionPanel />
        {canConfig && <BackupsPanel />}
        {canDirector && <PayWeekendsPanel />}
        {canDirector && <SeniorityModePanel />}
        {canConfig && <DepartmentsPanel />}
        {canConfig && <ShiftCodesPanel />}
        {canConfig && <RequestFieldsPanel />}
        {canConfig && <ShiftBidPanel />}
      </div>

    </div>
  );
}

// ----------------------------------------------------------------------
// Accounts — hierarchical user list + ownership transfer.

// Owner-only inline row that lives inside the Agency settings card.
// Renders nothing at all for non-Owner accounts so the box stays clean.
function BrandingRow() {
  const qc = useQueryClient();
  const me = useEffectiveUser();
  const img = useQuery({ queryKey: ["brandImage"], queryFn: api.getBrandImage });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const saveMut = useMutation({
    mutationFn: (dataUrl: string | null) => api.setBrandImage(dataUrl),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brandImage"] }),
  });

  if (!me || me.level !== "owner") return null;

  async function onFile(file: File) {
    setError(null);
    const MAX = 512 * 1024;
    if (file.size > MAX) {
      setError(`Image is too large (${Math.round(file.size / 1024)} KB). Max 512 KB.`);
      return;
    }
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = () => reject(new Error("Failed to read image."));
        r.readAsDataURL(file);
      });
      await saveMut.mutateAsync(dataUrl);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pt-1">
      <div className="text-xs font-medium text-slate-600 mb-1">
        Agency image
      </div>
      <div className="flex items-start gap-3">
        <div className="w-20 h-20 rounded border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
          {img.data ? (
            <img src={img.data} alt="Agency image"
              className="max-w-full max-h-full object-contain" />
          ) : (
            <span className="text-[11px] text-slate-400">none</span>
          )}
        </div>
        <div className="flex-1 space-y-1.5">
          <p className="text-xs text-slate-500">
            Appears in the sidebar. PNG or JPG, up to 512 KB.
          </p>
          <div className="flex items-center gap-2">
            <label className="inline-flex cursor-pointer">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onFile(f);
                  e.currentTarget.value = "";
                }}
                disabled={busy}
              />
              <span className="inline-flex items-center justify-center font-medium rounded-md px-2.5 py-1 text-sm bg-white border border-slate-300 hover:bg-slate-50 text-slate-900">
                {busy ? "Uploading…" : img.data ? "Replace" : "Choose image"}
              </span>
            </label>
            {img.data && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => saveMut.mutate(null)}
              >
                Remove
              </Button>
            )}
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Backups & restore. Owner / L1 only. Snapshots ride on top of
// snapshot_agency() in the DB; hourly cron writes the same shape.
// Last 8 entries (manual + auto combined) are retained.

function BackupsPanel() {
  const me = useEffectiveUser();
  const mayUse = can.viewSettings(me) && (me?.level === "owner" || me?.level === "l1");
  const qc = useQueryClient();
  const backups = useQuery({
    queryKey: ["backups"],
    queryFn: api.listBackups,
    enabled: mayUse,
  });
  const [label, setLabel] = useState("");
  const [restoreTarget, setRestoreTarget] = useState<{
    id: string; label: string; createdAt: string;
  } | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [flash, setFlash] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: (lbl: string | undefined) => api.createBackup(lbl),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["backups"] });
      setLabel("");
      setFlash("Backup created.");
      setErr(null);
    },
    onError: (e: { message?: string }) => setErr(e?.message ?? "Backup failed."),
  });
  const restoreMut = useMutation({
    mutationFn: (id: string) => api.restoreBackup(id),
    onSuccess: () => {
      qc.invalidateQueries();
      setRestoreTarget(null);
      setConfirmText("");
      setFlash("Restore complete. Reloading…");
      setErr(null);
      setTimeout(() => window.location.reload(), 1200);
    },
    onError: (e: { message?: string }) => {
      setErr(e?.message ?? "Restore failed.");
      setRestoreTarget(null);
      setConfirmText("");
    },
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteBackup(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["backups"] });
      setFlash("Backup deleted.");
      setErr(null);
    },
    onError: (e: { message?: string }) => setErr(e?.message ?? "Delete failed."),
  });

  if (!mayUse) return null;

  function downloadOne(id: string, createdAt: string) {
    api.downloadBackup(id)
      .then((snap) => {
        const blob = new Blob([JSON.stringify(snap, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `shiftmanager-backup-${createdAt.slice(0, 19).replace(/[:T]/g, "-")}.json`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((e: { message?: string }) => setErr(e?.message ?? "Download failed."));
  }

  const RESTORE_CONFIRM = "I understand this overwrites current data";

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <h2 className="font-medium text-slate-900">Backups</h2>
      <p className="text-sm text-slate-500 mt-1">
        Snapshots every hour automatically plus any manual ones you
        take here. Only the most recent 8 are kept — older entries
        roll off as new ones come in. Restoring wipes all current
        data in the snapshot's tables and replays from the saved
        copy.
      </p>

      <div className="mt-3 flex gap-2 items-end">
        <Field label="Optional label">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. before-bid-edits"
            disabled={createMut.isPending}
          />
        </Field>
        <Button
          onClick={() => createMut.mutate(label.trim() || undefined)}
          disabled={createMut.isPending}
        >
          {createMut.isPending ? "Saving…" : "Create backup now"}
        </Button>
      </div>

      {flash && <div className="mt-2 text-sm text-emerald-700">{flash}</div>}
      {err && <div className="mt-2 text-sm text-red-600">{err}</div>}

      <ul className="mt-4 divide-y divide-slate-100 border border-slate-200 rounded">
        {(backups.data ?? []).length === 0 ? (
          <li className="px-3 py-3 text-sm text-slate-500">No backups yet.</li>
        ) : (
          (backups.data ?? []).map((b) => (
            <li key={b.id} className="px-3 py-2 flex items-center gap-3 text-sm">
              <span
                className={
                  "inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded "
                  + (b.kind === "manual"
                    ? "bg-sky-50 text-sky-700 border border-sky-200"
                    : "bg-slate-100 text-slate-600 border border-slate-200")
                }
              >
                {b.kind}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-slate-800 font-medium truncate">
                  {b.label || (b.kind === "auto" ? "Hourly snapshot" : "Manual backup")}
                </div>
                <div className="text-xs text-slate-500">
                  {new Date(b.createdAt).toLocaleString()}
                  {b.sizeBytes != null && (
                    <span className="ml-2 text-slate-400">
                      · {formatBytes(b.sizeBytes)}
                    </span>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => downloadOne(b.id, b.createdAt)}
              >
                Download
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setRestoreTarget({
                  id: b.id,
                  label: b.label || (b.kind === "auto" ? "Hourly snapshot" : "Manual backup"),
                  createdAt: b.createdAt,
                })}
              >
                Restore
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => deleteMut.mutate(b.id)}
                disabled={deleteMut.isPending}
                title="Delete this backup"
              >
                ×
              </Button>
            </li>
          ))
        )}
      </ul>

      <Modal
        open={restoreTarget !== null}
        onClose={() => { setRestoreTarget(null); setConfirmText(""); }}
        title="Restore from backup?"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => { setRestoreTarget(null); setConfirmText(""); }}
              disabled={restoreMut.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => restoreTarget && restoreMut.mutate(restoreTarget.id)}
              disabled={restoreMut.isPending || confirmText !== RESTORE_CONFIRM}
            >
              {restoreMut.isPending ? "Restoring…" : "Restore"}
            </Button>
          </>
        }
      >
        {restoreTarget && (
          <div className="text-sm text-slate-700 space-y-3">
            <p>
              Replace all current data with the snapshot taken at{" "}
              <strong>{new Date(restoreTarget.createdAt).toLocaleString()}</strong>
              {" "}({restoreTarget.label}).
            </p>
            <p className="text-xs text-slate-500">
              This wipes the agency's current departments, shifts,
              shift codes, employees, schedule assignments,
              temporary shift placements, time-off requests, and
              training requests — and replays them from the snapshot.
              User accounts (login email + password) and the audit
              log are not touched.
            </p>
            <Field label={`Type "${RESTORE_CONFIRM}" to enable Restore`}>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={RESTORE_CONFIRM}
              />
            </Field>
          </div>
        )}
      </Modal>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// ----------------------------------------------------------------------
// Pay-period weekend highlights.

function PayWeekendsPanel() {
  const qc = useQueryClient();
  const current = useQuery({
    queryKey: ["payWeekendWeeks"],
    queryFn: api.getPayWeekendWeeks,
  });
  const selected = new Set(current.data ?? [0, 2]);

  const saveMut = useMutation({
    mutationFn: (weeks: number[]) => api.setPayWeekendWeeks(weeks),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payWeekendWeeks"] }),
  });

  function toggle(week: number) {
    const next = new Set(selected);
    if (next.has(week)) next.delete(week); else next.add(week);
    saveMut.mutate(Array.from(next));
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <h2 className="font-medium text-slate-900">Pay-period markers</h2>
      <p className="text-sm text-slate-500 mt-1">
        Which weeks on the schedule start a new pay period. For each picked
        week we highlight the opening Sunday and the closing Saturday of the
        two-week pay period in amber. Defaults to weeks 1 and 3 (bi-weekly
        pay) — so the schedule lights up the four bookend days of the two
        pay periods.
      </p>
      <div className="mt-3 flex flex-wrap gap-3">
        {[0, 1, 2, 3].map((w) => (
          <label
            key={w}
            className={
              "flex items-center gap-2 px-3 py-2 rounded border cursor-pointer text-sm " +
              (selected.has(w)
                ? "bg-amber-50 border-amber-300 text-amber-900"
                : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50")
            }
          >
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={selected.has(w)}
              onChange={() => toggle(w)}
            />
            Week {w + 1}
          </label>
        ))}
      </div>
      <p className="text-xs text-slate-400 mt-2">
        Weeks are numbered from the schedule's start date — they shift
        automatically when you change the "Start" field on the Schedule.
      </p>
    </div>
  );
}

// Picks which key drives the per-shift roster ordering on the
// Schedule grid. Most agencies sort by rank → badge (Sgt above
// Officer, lower badge numbers first); agencies that issue every
// patrol officer a numbered portable can flip to radio-first
// instead. Either way an officer without a radio number falls
// back to the rank+badge ordering, so civilians / OICs without
// a portable still slot into a sensible position.
function SeniorityModePanel() {
  const qc = useQueryClient();
  const current = useQuery({
    queryKey: ["seniorityMode"],
    queryFn: api.getSeniorityMode,
  });
  const mode = current.data ?? "rank_badge";

  const saveMut = useMutation({
    mutationFn: (m: "rank_badge" | "radio_rank_badge") => api.setSeniorityMode(m),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["seniorityMode"] }),
  });

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <h2 className="font-medium text-slate-900">Schedule row order</h2>
      <p className="text-sm text-slate-500 mt-1">
        Which key sorts officers inside a shift section on the
        Schedule grid. Switch to "Radio number" if your agency
        addresses officers by portable call sign on the air —
        rank + badge still acts as the tie-breaker for anyone
        without a radio number on file.
      </p>
      <div className="mt-3 space-y-2">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="radio"
            name="seniorityMode"
            className="mt-0.5 h-4 w-4"
            checked={mode === "rank_badge"}
            onChange={() => saveMut.mutate("rank_badge")}
          />
          <div>
            <div className="text-sm text-slate-800">Rank, then badge number</div>
            <div className="text-xs text-slate-500">
              Default. Chief / Major / Captain / Lieutenant / Sergeant / Corporal /
              Officer, with the lower badge number first inside each tier.
            </div>
          </div>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="radio"
            name="seniorityMode"
            className="mt-0.5 h-4 w-4"
            checked={mode === "radio_rank_badge"}
            onChange={() => saveMut.mutate("radio_rank_badge")}
          />
          <div>
            <div className="text-sm text-slate-800">Radio number, then rank, then badge</div>
            <div className="text-xs text-slate-500">
              Officers with a radio number sort by call sign first
              (lower numbers first). Officers without one fall back
              to rank + badge so dispatch and other unit-less staff
              still order sensibly.
            </div>
          </div>
        </label>
      </div>
    </div>
  );
}

// Owner-only billing card. Shows the subscription status, period
// end / trial info, and a button that opens the Stripe-hosted
// Customer Portal for cancellations and card / address updates.
// Renders nothing for non-owners — they don't need to see the
// agency's billing.
function SubscriptionPanel() {
  const me = useEffectiveUser();
  const subQ = useQuery({
    queryKey: ["my-subscription"],
    queryFn: api.getMySubscription,
    enabled: me?.level === "owner",
  });
  const portalMut = useMutation({
    mutationFn: () => api.startBillingPortal(),
    onSuccess: ({ url }) => { window.location.href = url; },
    onError: (e: { message?: string }) => setError(e?.message ?? "Couldn't open the billing portal."),
  });
  const [error, setError] = useState<string | null>(null);

  if (!me || me.level !== "owner") return null;

  const sub = subQ.data;
  const status = sub?.status ?? "none";
  const niceStatus =
    status === "active"   ? { label: "Active", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" } :
    status === "trialing" ? { label: "Trial",  cls: "text-sky-700 bg-sky-50 border-sky-200" } :
    status === "past_due" ? { label: "Past due", cls: "text-amber-800 bg-amber-50 border-amber-200" } :
    status === "canceled" ? { label: "Canceled", cls: "text-red-700 bg-red-50 border-red-200" } :
                            { label: status,    cls: "text-slate-600 bg-slate-50 border-slate-200" };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <header>
        <h2 className="font-medium text-slate-900">Subscription</h2>
        <p className="text-sm text-slate-500 mt-1">
          Manage your agency's plan, payment method, and invoices.
        </p>
      </header>

      <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
        <span className={`inline-block rounded border px-2 py-0.5 text-xs font-semibold ${niceStatus.cls}`}>
          {niceStatus.label}
        </span>
        {sub?.currentPeriodEnd && (
          <span className="text-slate-600">
            {sub.cancelAtPeriodEnd ? "Ends" : "Renews"}{" "}
            {new Date(sub.currentPeriodEnd).toLocaleDateString()}
          </span>
        )}
        {sub?.trialEnd && status === "trialing" && (
          <span className="text-slate-600">
            Trial ends {new Date(sub.trialEnd).toLocaleDateString()}
          </span>
        )}
      </div>

      {error && <div className="mt-3 text-sm text-red-700">{error}</div>}

      <div className="mt-4">
        {/* Stripe billing portal — disabled on iOS to comply with App
          * Store Guideline 3.1.3 (don't direct iOS users to external
          * payment methods). On native, the button shows greyed out
          * with a tooltip pointing the user to the web app. The web
          * build keeps the live button. */}
        <Button
          variant="secondary"
          onClick={() => portalMut.mutate()}
          disabled={!sub?.stripeCustomerId || portalMut.isPending || isNative}
          title={isNative
            ? "Open ShiftManager on the web to manage billing."
            : !sub?.stripeCustomerId
              ? "No Stripe customer on file yet — your subscription hasn't completed checkout."
              : "Open the Stripe Customer Portal to manage your subscription."}
        >
          {portalMut.isPending ? "Opening…" : "Manage subscription"}
        </Button>
      </div>
    </div>
  );
}

function TransferOwnershipPanel() {
  const qc = useQueryClient();
  const me = useEffectiveUser();
  const users = useQuery({ queryKey: ["users"], queryFn: api.listUsers });
  const [transferOpen, setTransferOpen] = useState(false);

  if (!me || !can.transferOwnership(me)) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <header>
        <h2 className="font-medium text-slate-900">Developer role</h2>
        <p className="text-sm text-slate-500 mt-1">
          Account access levels are edited from each employee's profile on
          the Employees page. Use this panel only to hand the Developer
          role to another account.
        </p>
      </header>

      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded">
        <h3 className="font-medium text-amber-900">Transfer Developer role</h3>
        <p className="text-sm text-amber-900/80 mt-1">
          Promotes another account to Developer and demotes you to Level 1.
          This cannot be undone by you afterwards.
        </p>
        {!transferOpen ? (
          <div className="mt-3">
            <Button variant="secondary" onClick={() => setTransferOpen(true)}>
              Begin transfer…
            </Button>
          </div>
        ) : (
          <TransferForm
            me={me}
            users={(users.data ?? []).filter((u) => u.id !== me.id && u.level !== "owner")}
            onCancel={() => setTransferOpen(false)}
            onDone={() => {
              setTransferOpen(false);
              qc.invalidateQueries({ queryKey: ["users"] });
              qc.invalidateQueries({ queryKey: ["currentUser"] });
            }}
          />
        )}
      </div>
    </div>
  );
}

function TransferForm({
  me, users, onCancel, onDone,
}: {
  me: User;
  users: User[];
  onCancel: () => void;
  onDone: () => void;
}) {
  const [target, setTarget] = useState<string>(users[0]?.id ?? "");
  const [ack, setAck] = useState(false);
  const [sentence, setSentence] = useState("");
  const [error, setError] = useState<string | null>(null);

  const valid = ack && sentence.trim() === OWNERSHIP_CONFIRM_SENTENCE && target;

  async function onSubmit() {
    try {
      await api.transferOwnership({
        fromUserId: me.id,
        toUserId: target,
        confirmation: sentence.trim(),
      });
      onDone();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="mt-3 space-y-3">
      <Field label="Transfer Developer role to">
        <Select value={target} onChange={(e) => setTarget(e.target.value)}>
          {users.length === 0 && <option value="">— No other accounts —</option>}
          {users.map((u) => (
            <option key={u.id} value={u.id}>{u.username} · {LEVEL_LABEL[u.level]}</option>
          ))}
        </Select>
      </Field>
      <label className="flex items-start gap-2 text-sm text-amber-900">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4"
          checked={ack}
          onChange={(e) => setAck(e.target.checked)}
        />
        <span>
          I understand that once transferred I become Level 1 and the new
          Developer has full control, including the ability to remove me.
        </span>
      </label>
      <Field label={`Type the sentence exactly: "${OWNERSHIP_CONFIRM_SENTENCE}"`}>
        <Input
          value={sentence}
          onChange={(e) => setSentence(e.target.value)}
          placeholder={OWNERSHIP_CONFIRM_SENTENCE}
        />
      </Field>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button variant="danger" disabled={!valid || users.length === 0}
          onClick={onSubmit}>
          Transfer Developer role
        </Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Departments — each gets its own schedule tab in the sidebar.

function DepartmentsPanel() {
  const qc = useQueryClient();
  const depts = useQuery({ queryKey: ["departments"], queryFn: api.listDepartments });
  const [draft, setDraft] = useState<Partial<Department> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Department | null>(null);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["departments"] });
    qc.invalidateQueries({ queryKey: ["employees"] });
    qc.invalidateQueries({ queryKey: ["shifts"] });
  }

  const saveMut = useMutation({
    mutationFn: async (d: Partial<Department>) => {
      if (!d.name?.trim()) throw new Error("Name required");
      if (d.id) return api.updateDepartment(d.id, d.name);
      return api.createDepartment(d.name);
    },
    onSuccess: () => { invalidate(); setDraft(null); setError(null); },
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed"),
  });

  return (
    <div id="departments" className="bg-white border border-slate-200 rounded-lg p-5">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="font-medium text-slate-900">Departments</h2>
          <p className="text-sm text-slate-500 mt-1">
            Each department gets its own schedule tab in the left sidebar with
            its own roster and its own shifts. Think Patrol, Dispatch, Animal
            Control.
          </p>
        </div>
        <Button size="sm" onClick={() => { setDraft({ name: "" }); setError(null); }}>
          + New department
        </Button>
      </header>
      {error && (
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
      )}

      <div className="mt-4 border border-slate-200 rounded max-h-72 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 font-medium">Name</th>
              <th className="px-3 py-2 w-28"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(depts.data ?? []).map((d) => (
              <tr key={d.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-medium">{d.name}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost"
                    onClick={() => { setDraft(d); setError(null); }}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost"
                    onClick={() => { setDeleteTarget(d); setError(null); }}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
            {(depts.data ?? []).length === 0 && (
              <tr>
                <td colSpan={2} className="p-6 text-center text-slate-500 text-sm">
                  No departments yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {deleteTarget && (
        <DeleteDepartmentModal
          target={deleteTarget}
          otherDepartments={(depts.data ?? []).filter((x) => x.id !== deleteTarget.id)}
          onClose={() => setDeleteTarget(null)}
          onDone={() => { invalidate(); setDeleteTarget(null); }}
          onError={(m) => { setDeleteTarget(null); setError(m); }}
        />
      )}

      {draft && (
        <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded space-y-3">
          <Field label="Name">
            <Input
              autoFocus
              value={draft.name ?? ""}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Dispatch"
            />
          </Field>
          {error && <div className="text-sm text-red-600">{error}</div>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDraft(null)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate(draft)} disabled={saveMut.isPending}>
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// Shift codes — editable list shown in the cell-picker dropdown.

function ShiftCodesPanel() {
  const qc = useQueryClient();
  const codes = useQuery({ queryKey: ["shiftCodes"], queryFn: api.listShiftCodes });
  const [draft, setDraft] = useState<Partial<ShiftCode> | null>(null);
  const [armedDeleteId, setArmedDeleteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["shiftCodes"] });
    qc.invalidateQueries({ queryKey: ["coverage"] });
  }

  const saveMut = useMutation({
    mutationFn: (c: Partial<ShiftCode>) =>
      c.id ? api.updateShiftCode(c.id, c) : api.createShiftCode(c),
    onSuccess: () => { invalidate(); setDraft(null); setError(null); },
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed"),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteShiftCode(id),
    onSuccess: () => { invalidate(); setArmedDeleteId(null); setError(null); },
    onError: (e: { message?: string }) => {
      setArmedDeleteId(null);
      setError(e?.message ?? "Failed");
    },
  });

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="font-medium text-slate-900">Shift codes</h2>
          <p className="text-sm text-slate-500 mt-1">
            Codes that appear in the cell dropdown on the Schedule page.
            Coverage = how much that cell counts toward the shift's required
            staffing (0 = off, 0.5 = partial, 1 = working).
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setDraft({
            code: "",
            label: "",
            coverageValue: 0,
            sortOrder: codes.data?.length ?? 0,
          })}
        >
          + New code
        </Button>
      </header>
      {error && (
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
      )}

      <div className="mt-4 border border-slate-200 rounded max-h-72 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 font-medium w-20">Code</th>
              <th className="px-3 py-2 font-medium">Meaning</th>
              <th className="px-3 py-2 font-medium w-28">Coverage</th>
              <th className="px-3 py-2 w-28"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(codes.data ?? []).map((c) => (
              <tr key={c.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-mono font-semibold">{c.code}</td>
                <td className="px-3 py-2">{c.label || <span className="text-slate-400">—</span>}</td>
                <td className="px-3 py-2">
                  <CoverageChip value={c.coverageValue} />
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setDraft(c)}>Edit</Button>
                  <RemoveButton
                    armed={armedDeleteId === c.id}
                    onArm={() => setArmedDeleteId(c.id)}
                    onConfirm={() => deleteMut.mutate(c.id)}
                    label="Delete"
                    confirmLabel={`Confirm delete "${c.code}"`}
                    title="Existing assignments stay but lose their meaning."
                  />
                </td>
              </tr>
            ))}
            {(codes.data ?? []).length === 0 && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-slate-500 text-sm">
                  No codes yet. Add one to populate the cell dropdown.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {draft && (
        <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Code (uppercase, max 8 chars)">
              <Input
                value={draft.code ?? ""}
                onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
                maxLength={8}
              />
            </Field>
            <Field label="Meaning">
              <Input
                value={draft.label ?? ""}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder="e.g. Sick"
              />
            </Field>
            <Field label="Coverage value">
              <Select
                value={String(draft.coverageValue ?? 0)}
                onChange={(e) =>
                  setDraft({ ...draft, coverageValue: Number(e.target.value) })
                }
              >
                <option value="0">0 — off</option>
                <option value="0.5">0.5 — partial</option>
                <option value="1">1 — working</option>
              </Select>
            </Field>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDraft(null)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate(draft)} disabled={saveMut.isPending}>
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CoverageChip({ value }: { value: number }) {
  const cls =
    value === 0   ? "bg-red-100 text-red-800" :
    value === 1   ? "bg-emerald-100 text-emerald-800" :
                    "bg-amber-100 text-amber-800";
  const label =
    value === 0   ? "Off" :
    value === 1   ? "Working" :
                    `Partial (${value})`;
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${cls}`}>
      {label}
    </span>
  );
}

// Admin-editable catalogue of fields rendered on the request forms.
// Training and Time off each have their own scope; a segmented
// switcher at the top of the panel picks which one we're editing.
// Values land in time_off.metadata, keyed by `key`. The RLS policy
// already limits writes to Owner / L1 — the whole Settings page is
// restricted to that bracket, so we don't need an extra gate here.
function RequestFieldsPanel() {
  const qc = useQueryClient();
  const [scope, setScope] = useState<RequestFieldScope>("training");
  const fields = useQuery({
    queryKey: ["requestFields", scope],
    queryFn: () => api.listRequestFields(scope),
  });
  const [draft, setDraft] = useState<Partial<RequestField> | null>(null);
  const [armedDeleteId, setArmedDeleteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["requestFields", scope] });
  }

  const saveMut = useMutation({
    mutationFn: (f: Partial<RequestField>) =>
      f.id
        ? api.updateRequestField(f.id, {
            key: f.key,
            label: f.label,
            fieldType: f.fieldType,
            sortOrder: f.sortOrder,
            required: f.required,
          })
        : api.createRequestField({
            scope,
            key: (f.key ?? "").trim() || keyFromLabel(f.label ?? ""),
            label: f.label ?? "",
            fieldType: (f.fieldType ?? "text") as RequestFieldType,
            sortOrder: f.sortOrder ?? (fields.data?.length ?? 0) * 10,
            required: f.required ?? false,
          }),
    onSuccess: () => { invalidate(); setDraft(null); setError(null); },
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed"),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteRequestField(id),
    onSuccess: () => { invalidate(); setArmedDeleteId(null); setError(null); },
    onError: (e: { message?: string }) => {
      setArmedDeleteId(null);
      setError(e?.message ?? "Failed");
    },
  });

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-medium text-slate-900">Request fields</h2>
          <p className="text-sm text-slate-500 mt-1">
            Custom fields shown on each request form. Values are stored
            with the request and visible to reviewers.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => { setDraft(null); setError(null); setDraft({
            label: "",
            key: "",
            fieldType: "text",
            required: false,
            sortOrder: (fields.data?.length ?? 0) * 10,
          }); }}
        >
          + New field
        </Button>
      </header>

      <div className="mt-3 inline-flex rounded border border-slate-300 overflow-hidden text-sm">
        {(["training", "time_off"] as RequestFieldScope[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => { setScope(s); setDraft(null); setError(null); }}
            className={
              "px-3 py-1 font-medium " +
              (scope === s
                ? "bg-slate-800 text-white"
                : "bg-white text-slate-700 hover:bg-slate-50")
            }
          >
            {s === "training" ? "Training" : "Time off"}
          </button>
        ))}
      </div>
      {error && (
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
      )}

      <div className="mt-4 border border-slate-200 rounded max-h-72 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 font-medium">Label</th>
              <th className="px-3 py-2 font-medium w-32">Type</th>
              <th className="px-3 py-2 font-medium w-24">Required</th>
              <th className="px-3 py-2 font-medium w-20">Order</th>
              <th className="px-3 py-2 w-28"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(fields.data ?? []).map((f) => (
              <tr key={f.id} className="hover:bg-slate-50">
                <td className="px-3 py-2">
                  <div className="font-medium">{f.label}</div>
                  <div className="text-xs text-slate-500 font-mono">{f.key}</div>
                </td>
                <td className="px-3 py-2 capitalize">{f.fieldType}</td>
                <td className="px-3 py-2">
                  {f.required ? (
                    <span className="text-xs font-semibold text-amber-700">
                      Required
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">Optional</span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">
                  {f.sortOrder}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setDraft(f)}>
                    Edit
                  </Button>
                  <RemoveButton
                    armed={armedDeleteId === f.id}
                    onArm={() => setArmedDeleteId(f.id)}
                    onConfirm={() => deleteMut.mutate(f.id)}
                    label="Delete"
                    confirmLabel={`Confirm delete "${f.label}"`}
                    title="Values already saved on past requests stay in their metadata."
                  />
                </td>
              </tr>
            ))}
            {(fields.data ?? []).length === 0 && (
              <tr>
                <td colSpan={5} className="p-6 text-center text-slate-500 text-sm">
                  No fields yet. Add one to start collecting training details.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {draft && (
        <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="Label">
              <Input
                value={draft.label ?? ""}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder="e.g. Course Title"
              />
            </Field>
            <Field label="Key (auto from label if blank)">
              <Input
                value={draft.key ?? ""}
                onChange={(e) => setDraft({ ...draft, key: e.target.value })}
                placeholder="e.g. course_title"
              />
            </Field>
            <Field label="Type">
              <Select
                value={draft.fieldType ?? "text"}
                onChange={(e) => setDraft({
                  ...draft,
                  fieldType: e.target.value as RequestFieldType,
                })}
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="checkbox">Checkbox</option>
              </Select>
            </Field>
            <Field label="Sort order">
              <Input
                type="number"
                value={draft.sortOrder ?? 0}
                onChange={(e) =>
                  setDraft({ ...draft, sortOrder: Number(e.target.value) })
                }
              />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={draft.required ?? false}
              onChange={(e) =>
                setDraft({ ...draft, required: e.target.checked })
              }
            />
            Required on submit
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => saveMut.mutate(draft)}
              disabled={saveMut.isPending || !(draft.label ?? "").trim()}
            >
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function keyFromLabel(label: string): string {
  return label.trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// Red banner for surface-level error messages from mutations inside
// a panel. Rendered near the top of the panel so users don't miss
// the reason a delete or save didn't take effect. We route errors
// here rather than through alert() because some browser lockdown
// scripts / SES environments silently suppress window.alert.
function ErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="mt-3 flex items-start justify-between gap-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
      <span className="flex-1">{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="text-red-400 hover:text-red-700 font-semibold leading-none"
        title="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

// Confirm-and-reassign flow for department deletion. The FK on
// employees.department_id / shifts.department_id is NOT NULL with
// ON DELETE CASCADE, so deleting a department outright would
// destroy the attached rows. This modal surfaces the attached
// counts and forces the user to pick another department to move
// them onto first; api.deleteDepartment then does the UPDATE before
// the DELETE. Empty departments skip the picker.
function DeleteDepartmentModal({
  target, otherDepartments, onClose, onDone, onError,
}: {
  target: Department;
  otherDepartments: Department[];
  onClose: () => void;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const deps = useQuery({
    queryKey: ["deptDeps", target.id],
    queryFn: () => api.countDepartmentDependencies(target.id),
  });
  const [reassignTo, setReassignTo] = useState<string>(otherDepartments[0]?.id ?? "");
  const deleteMut = useMutation({
    mutationFn: (opts?: { reassignTo?: string }) =>
      api.deleteDepartment(target.id, opts),
    onSuccess: onDone,
    onError: (e: { message?: string }) => onError(e?.message ?? "Delete failed"),
  });

  const hasDeps = (deps.data?.empCount ?? 0) > 0 || (deps.data?.shiftCount ?? 0) > 0;
  const onlyDept = otherDepartments.length === 0;

  return (
    <Modal
      open={true}
      onClose={() => { if (!deleteMut.isPending) onClose(); }}
      title={`Delete ${target.name}?`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={deleteMut.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!hasDeps) return deleteMut.mutate(undefined);
              if (!reassignTo) return;
              deleteMut.mutate({ reassignTo });
            }}
            disabled={
              deleteMut.isPending
              || deps.isLoading
              || onlyDept
              || (hasDeps && !reassignTo)
            }
          >
            {deleteMut.isPending
              ? "Deleting…"
              : hasDeps
                ? "Reassign and delete"
                : "Delete"}
          </Button>
        </>
      }
    >
      {deps.isLoading ? (
        <p className="text-sm text-slate-500">Checking what's attached…</p>
      ) : onlyDept ? (
        <p className="text-sm text-red-700">
          You can't delete the last department. Create another one first.
        </p>
      ) : !hasDeps ? (
        <p className="text-sm text-slate-700">
          No employees or shifts are attached to this department. Delete it?
        </p>
      ) : (
        <div className="space-y-3 text-sm">
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
            <strong>{target.name}</strong> has{" "}
            <strong>{deps.data!.empCount}</strong> employee
            {deps.data!.empCount === 1 ? "" : "s"} and{" "}
            <strong>{deps.data!.shiftCount}</strong> shift
            {deps.data!.shiftCount === 1 ? "" : "s"} attached.
            Deleting will move them to the department you pick below.
          </div>
          <Field label="Move employees and shifts to">
            <Select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}>
              {otherDepartments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </Select>
          </Field>
        </div>
      )}
    </Modal>
  );
}

// ----------------------------------------------------------------------
// Shift Bid: bulk-edit every officer's weekly days-off pattern with a
// shared start date. Meant for the biannual bid event where patterns
// change agency-wide in one sitting.

function ShiftBidPanel() {
  const me = useEffectiveUser();
  const mayEdit = can.editEmployees(me);
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
      <div>
        <h2 className="font-medium text-slate-900">Shift bid</h2>
        <p className="text-sm text-slate-500">
          Update every officer's days off in one pass — typically after a
          biannual shift bid. Pattern changes auto-fill XXX on future
          schedule pages from the effective start date forward. Manual
          edits on already-scheduled days are preserved.
        </p>
      </div>
      <div>
        <Button onClick={() => setOpen(true)} disabled={!mayEdit}>
          Open shift bid
        </Button>
      </div>
      {open && <ShiftBidModal onClose={() => setOpen(false)} />}
    </div>
  );
}

function ShiftBidModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const employees = useQuery({ queryKey: ["employees"], queryFn: () => api.listEmployees() });
  const shifts = useQuery({ queryKey: ["shifts"], queryFn: () => api.listShifts() });
  const [startDate, setStartDate] = useState(todayStr());
  const [drafts, setDrafts] = useState<Record<string, number[]>>({});
  // Per-officer pending home-shift change. "" = keep current.
  const [shiftDrafts, setShiftDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  // Seed drafts from the officers' current patterns once loaded.
  useEffect(() => {
    if (!employees.data) return;
    setDrafts((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const next: Record<string, number[]> = {};
      for (const e of employees.data!) {
        next[e.id] = [...(e.daysOffWeekdays ?? [])];
      }
      return next;
    });
    setShiftDrafts((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const next: Record<string, string> = {};
      for (const e of employees.data!) {
        next[e.id] = e.homeShiftId ?? "";
      }
      return next;
    });
  }, [employees.data]);

  const active = (employees.data ?? [])
    .filter((e) => e.status !== "inactive" && !e.deletedAt)
    .sort((a, b) =>
      (a.lastName ?? "").localeCompare(b.lastName ?? "")
      || (a.firstName ?? "").localeCompare(b.firstName ?? ""));

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!startDate) throw new Error("Pick an effective start date.");
      let done = 0;
      for (const e of active) {
        const wd = drafts[e.id] ?? [];
        const oldShiftId = e.homeShiftId;
        const draftShift = shiftDrafts[e.id] ?? "";
        const newShiftId = draftShift === "" ? null : draftShift;
        const shiftChanged = (newShiftId ?? "") !== (oldShiftId ?? "");

        // Step 1: migrate existing home-shift assignments from the
        // effective date forward onto the new shift so future XXX /
        // custom rows follow the officer. Do this before updating
        // home_shift_id so the "oldShiftId" lookup still matches.
        if (shiftChanged && oldShiftId && newShiftId) {
          await api.moveOfficerToShift({
            employeeId: e.id,
            oldShiftId,
            newShiftId,
            startDate,
            // Shift Bid is a bulk action; skip per-officer email
            // notifications here (user-requested).
            silent: true,
          });
        }

        // Step 2: update the employee row — new pattern + start
        // date, and the new home shift if changed. Tracked even for
        // officers receiving an empty / no-pattern assignment this
        // bid, so their current pattern is always in sync.
        const patch: Partial<Employee> = {
          daysOffWeekdays: wd,
          daysOffStartDate: wd.length > 0 ? startDate : null,
        };
        if (shiftChanged) patch.homeShiftId = newShiftId;
        await api.updateEmployee(e.id, patch);

        // Step 3: materialize XXX rows on the NEW shift if a pattern
        // is set. applyDaysOffPattern also cleans up stale from_pattern
        // rows on the old shift within the window. Shift Bid only
        // edits the regular week-1 pattern (it's a single-week,
        // single-mode bulk tool — tac / week-2 stay on the
        // Employees edit modal).
        if (wd.length > 0 && newShiftId) {
          await api.applyDaysOffPattern({
            employeeId: e.id,
            week1: wd,
            startDate,
            homeShiftId: newShiftId,
            silent: true,
          });
        }

        done += 1;
        setProgress(`Updating ${done} of ${active.length}…`);
      }
      setProgress(null);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["assignments"] });
      qc.invalidateQueries({ queryKey: ["coverage"] });
      onClose();
    },
    onError: (e: { message?: string }) => {
      setProgress(null);
      setError(e?.message ?? "Failed to apply shift bid.");
    },
  });

  function toggleDay(empId: string, day: number) {
    setDrafts((prev) => {
      const cur = new Set(prev[empId] ?? []);
      if (cur.has(day)) cur.delete(day); else cur.add(day);
      return { ...prev, [empId]: Array.from(cur).sort((a, b) => a - b) };
    });
  }

  const LABELS = ["S", "M", "T", "W", "T", "F", "S"];

  return (
    <Modal
      open={true}
      onClose={() => { if (!saveMut.isPending) onClose(); }}
      title="Shift bid — days off"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saveMut.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => { setError(null); saveMut.mutate(); }}
            disabled={saveMut.isPending || !startDate || employees.isLoading}
          >
            {saveMut.isPending ? (progress ?? "Saving…") : "Apply to all"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex items-center gap-3 border-b border-slate-200 pb-3">
          <label className="text-sm font-medium text-slate-700">
            Effective start date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          />
          <span className="text-xs text-slate-500">
            Patterns apply from this date forward.
          </span>
        </div>

        {error && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}

        {employees.isLoading ? (
          <p className="text-sm text-slate-500">Loading employees…</p>
        ) : active.length === 0 ? (
          <p className="text-sm text-slate-500">No active employees.</p>
        ) : (
          <div className="max-h-[60vh] overflow-auto border border-slate-200 rounded">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 text-xs text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Officer</th>
                  <th className="text-left px-3 py-2 font-semibold">Shift</th>
                  {LABELS.map((l, i) => (
                    <th key={i} className="px-1.5 py-2 font-semibold text-center w-9" title={["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][i]}>
                      {l}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {active.map((e) => {
                  const wd = new Set(drafts[e.id] ?? []);
                  const draftShift = shiftDrafts[e.id] ?? (e.homeShiftId ?? "");
                  const shiftChanged = draftShift !== (e.homeShiftId ?? "");
                  return (
                    <tr key={e.id} className="hover:bg-slate-50">
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        <span className="font-medium text-slate-900">
                          {e.lastName}, {e.firstName}
                        </span>
                        {e.isOic && (
                          <span className="ml-1.5 inline-block px-1 py-0 text-[9px] font-semibold rounded bg-amber-100 text-amber-800 border border-amber-200">
                            OIC
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <select
                          value={draftShift}
                          onChange={(ev) => setShiftDrafts((prev) => ({
                            ...prev,
                            [e.id]: ev.target.value,
                          }))}
                          className={
                            "text-xs rounded border px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400 " +
                            (shiftChanged
                              ? "border-sky-400 text-sky-800 bg-sky-50 font-medium"
                              : "border-slate-300 text-slate-700")
                          }
                          title={shiftChanged ? `Change from ${shifts.data?.find((s) => s.id === e.homeShiftId)?.name ?? "none"} on the effective date` : undefined}
                        >
                          <option value="">— None —</option>
                          {(shifts.data ?? [])
                            .filter((s) => !e.departmentId || s.departmentId === e.departmentId)
                            .map((s) => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                      </td>
                      {LABELS.map((_, day) => {
                        const active = wd.has(day);
                        return (
                          <td key={day} className="px-1 py-1 text-center">
                            <button
                              type="button"
                              onClick={() => toggleDay(e.id, day)}
                              className={
                                "w-7 h-7 rounded text-xs font-semibold border transition " +
                                (active
                                  ? "bg-slate-800 text-white border-slate-800"
                                  : "bg-white text-slate-400 border-slate-300 hover:bg-slate-100")
                              }
                              title={active ? "Marked off" : "Working"}
                            >
                              {active ? "×" : ""}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

function todayStr(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
